import { getStyleElementsBy, CONSTRUCTED_CSS_ID_PREFIX } from './renderer/tree-mirror';
import { HJ_SHADOW_PREFIX } from '../../constants/shadow-dom';

const BASE_EL_ID = '#svg-actions-container';
const cssHoverAttribute = 'hotjar-css-hover';
const mutationStyleTagIDs = []; // Used to retain a list of style tags added as mutations to the recording
const MOUSE_TRAIL_COLOR = '#FF3C00';
const FIRST_STYLESHEET_INSERT_INDEX = 0;

let sceneWindow = null;
let sceneDocument = null;
let cursor = null;
let lastHoveredElement = null;
let currentDevice;

/**
 * Module related to handling TreeMirror.
 */

/**
 * Module related to helper functions for the main playback script.
 */
const PlaybackHelpers = function () {
  const self = {};

  self.setVal = function (element, value) {
    if (typeof value !== 'undefined') {
      element.value = value;
    }
    return element !== undefined ? element.value : '';
  };

  self.getOffset = function (element) {
    const doc = element.ownerDocument.documentElement;
    return {
      top: element.getBoundingClientRect().top + sceneWindow.pageYOffset - doc.clientTop,
      left: element.getBoundingClientRect().left + sceneWindow.pageXOffset - doc.clientLeft,
    };
  };

  return self;
};

/**
 * The main playback script module.
 */
export const PlaybackScript = function () {
  const MOUSE_TRAIL_ANIMATION = {
    PLAY_CLASS: 'hide-after-delay',
    PAUSE_CLASS: 'pause-animation',
    DELAY: 5,
    DURATION: 5,
    PAUSE_DUE_TO_INACTIVITY_TIME: 4,
  };
  const h = new PlaybackHelpers();

  let lastMouseMoveCoords = null; // old mouse move coords - used when drawing the tracer line
  let mouseMoveCorrectionOffsets = { x: 0, y: 0 }; // holds any differences between mouse moves and clicks
  let expandedSelect = null;
  let hasScrolled = false; // flag so we know how to correctly update the mouse trace line
  let pageVisitRecordings;
  let playbackStartTime;
  let playbackSpeed = 1;
  let lastMouseMoveTime = 0;
  let animationPaused = false;
  let currentRecordingWidth;
  let onInactiveTickDoneCb = () => {
    // noop
  };
  let onRetrieveHtmlResponseCb = () => {
    // noop
  };
  let onEventsProcessedCb = () => {
    // noop
  };

  /*****************
   * PRIVATE FUNCTIONS
   *****************/
  function handleKeyPressEvent(playbackEvent) {
    const element = getElementByCustomSelector(playbackEvent.selector);

    if (typeof element === 'undefined') {
      return;
    }

    h.setVal(element, playbackEvent.text);
  }

  function isScreenshotMode() {
    return Boolean(new URLSearchParams(window.document.location.search).get('forScreenshot'));
  }

  function handleSelectChangeEvent(playbackEvent) {
    const element = getElementByCustomSelector(playbackEvent.selector);

    if (typeof element === 'undefined') {
      return;
    }

    h.setVal(element, playbackEvent.value);
    element.dispatchEvent(new Event('change'));

    // Close the cloned select menu.
    if (expandedSelect) {
      closeSelect();
    }
  }

  function handleInputChoiceChangeEvent(playbackEvent) {
    const element = getElementByCustomSelector(playbackEvent.selector);

    if (typeof element === 'undefined') {
      return;
    }

    if (playbackEvent.value) {
      element.checked = playbackEvent.value;
    } else {
      element.checked = false;
    }
  }

  function handleMouseMoveEvent(playbackEvent) {
    let elementFromPoint;
    let hoveredElement;

    // If device screen is mobile or tablet, exit.
    if (currentDevice !== 'desktop') {
      return;
    }

    elementFromPoint = sceneDocument.elementFromPoint(playbackEvent.x, playbackEvent.y);

    // To avoid multiple appending and removal of classes,
    // we only run the following code if the the current elementFromPoint
    // is different from the previous mouse move event.
    if (elementFromPoint !== lastHoveredElement) {
      // Remove the CSS hover attribute from any elements that currently have it set.
      sceneDocument.querySelectorAll('[' + cssHoverAttribute + ']').forEach(function (element) {
        element.removeAttribute(cssHoverAttribute);
      });

      // Apply the CSS hover attribute to the topmost element under the mouse pointer,
      // and recursively apply the attribute to all of its parents as well.
      // We use attribute instead of a class name to prevent unnecessary repaints.
      hoveredElement = elementFromPoint;
      while (hoveredElement) {
        try {
          hoveredElement.setAttribute(cssHoverAttribute, true);
        } catch (e) {
          // Not all elements have a class name setter.
        }

        hoveredElement = hoveredElement.parentNode;
      }

      lastHoveredElement = elementFromPoint;
    }

    updateCursorCoordinates(
      {
        x: playbackEvent.x,
        y: playbackEvent.y,
      },
      playbackEvent.time_in_timeline,
    );
  }

  function handleMouseClickEvent(playbackEvent) {
    let element = getElementByCustomSelector(playbackEvent.selector);

    if (typeof element === 'undefined') {
      return;
    }

    // HACK: <select> elements need special handling since click() events do not expand them.
    // Mimic browser behavior:

    if (expandedSelect) {
      closeSelect();
    }

    if (element.tagName === 'SELECT') {
      // Show a list of options if a new <select> element is clicked.
      expandSelect(element);
      expandedSelect = element;
    }

    const elementOffset = h.getOffset(element);
    // Position relative to document = element click position (relative to element) +
    // element offset in relation to document
    const coords = {
      x: playbackEvent.offset_x + elementOffset.left,
      y: playbackEvent.offset_y + elementOffset.top,
    };

    // If the event contains coords relative to the document, calculate any
    // adjustments that may be needed to mouse moves, based on them. Otherwise,
    // use the last mouse movement coords.
    mouseMoveCorrectionOffsets = {
      x: coords.x - (playbackEvent.page_x || lastMouseMoveCoords.x),
      y: coords.y - (playbackEvent.page_y || lastMouseMoveCoords.y),
    };

    // Place the click marker if it shouldn't have been hidden.
    if (shouldTrailBeUpdated(playbackEvent.time_in_timeline)) {
      // This, in case there is a difference between the last mouse movement and this click,
      // will draw a line that connects the last line drawn with the click.
      // Remember here, `updateCursorCoordinates` accepts coordinates relative to the
      // viewport.
      updateCursorCoordinates(
        {
          x:
            coords.x -
            (sceneDocument.body.scrollLeft || sceneWindow.pageXOffset) -
            mouseMoveCorrectionOffsets.x,
          y:
            coords.y -
            (sceneDocument.body.scrollTop || sceneWindow.pageYOffset) -
            mouseMoveCorrectionOffsets.y,
        },
        playbackEvent.time_in_timeline,
      );
      createClickMarker(coords);
    }
  }

  function handleScrollEvent(event) {
    // This is to handle legacy recordings that don't provide an elem.
    if (!event.elem) {
      event.elem = 'window';
    }

    // This is to handle legacy recordings that don't provide an x scroll position.
    if (!event.x) {
      event.x = 0;
    }

    hasScrolled = true;

    if (event.elem === 'window') {
      sceneWindow.scrollTo(event.x, event.y);

      // Scroll the window in #playback-controller as the canvas is there.
      document.querySelector(BASE_EL_ID).scrollTo(event.x, event.y);
    } else {
      getElementByCustomSelector(event.elem).scrollTo(event.x, event.y);
    }
  }

  function uniqueValues(value, index, self) {
    return self.indexOf(value) === index;
  }

  /**
   * Will look for all the style tags that were part of the event and re-write all CSS rules associated for each
   * style tag as text content.
   *
   * This will persist the style as text instead of just in memory so when we are taking a snapshot of the page
   * the styles are copied over.
   *
   * Useful when we need to  take a screenshot from a recording for continuous capture heatmaps.
   */
  function hydrateStyleTag(event) {
    const uniqueParentSelectors = event.rules
      .map((rule) => rule.parentSelector)
      .filter(uniqueValues);
    uniqueParentSelectors.map(getElementByCustomSelector).forEach((styleElement) => {
      const styleSheet = styleElement.sheet;
      styleElement.textContent = [...styleSheet.cssRules]
        .map((rule) => `${rule.cssText} `)
        .join('');
    });
  }

  function handleInsertedRule(event, startPageContentIdentifier, recordingId) {
    event.rules.forEach(function (ruleSet) {
      // Do this in a try/catch so if one rule fails the whole rules array doesn't fail.
      try {
        const nodeId = ruleSet.nodeId;
        const node = window.iframeController
          .getTreeMirror(recordingId, startPageContentIdentifier)
          .getNodeById(nodeId);
        let styleElem = node ?? getElementByCustomSelector(ruleSet.parentSelector),
          styleSheet,
          insertIndex;

        if (!styleElem) {
          return;
        }

        // If we have a ShadowRoot, than the current rule is targeting a ShadowDOM.
        /*
                    Note
                    When we'll provide support for AdoptedStyleSheet in the Document
                    we need an extra variable to differentiate it with a Shadow DOM
                */
        if (styleElem.shadowRoot) {
          styleElem = styleElem.shadowRoot.querySelector('style');
        }

        styleSheet = styleElem.sheet;

        // @hotfix for RECS-1095 https://hotjar.atlassian.net/browse/RECS-1095.
        // CSS @import rule must be inserted always at the top of the stylesheet.
        const isImportRule = ruleSet.rule.includes('@import');

        // This happens if the events index is out of sync with the length of the styleSheet.cssRules.
        // In this case we just insert the rule at the end of the stylesheet rather than ignoring it
        insertIndex = isImportRule
          ? FIRST_STYLESHEET_INSERT_INDEX
          : Math.min(styleSheet.cssRules.length, ruleSet.index);

        // Update the insert rule so we replace :hover pseudo-classes with our own attribute.
        // We use attribute instead of a class name to prevent unnecessary repaints.
        styleSheet.insertRule(
          ruleSet.rule.replace(/:hover/g, '[' + cssHoverAttribute + ']'),
          insertIndex,
        );
      } catch (e) {
        /* eslint-disable no-console */
        // The following line is required for debugging purposes.
        // nosemgrep
        console.error(e, ruleSet);
        /* eslint-enable no-console */
      }
    });

    if (isScreenshotMode()) {
      hydrateStyleTag(event);
    }
  }

  function handleDeletedRule(event, startPageContentIdentifier, recordingId) {
    event.rules.forEach(function (ruleSet) {
      // Do this in a try/catch so if one rule fails the whole rules array doesn't fail.
      try {
        const node = window.iframeController
          .getTreeMirror(recordingId, startPageContentIdentifier)
          .getNodeById(ruleSet.nodeId);
        let styleElem = node ?? getElementByCustomSelector(ruleSet.parentSelector);
        let styleSheet;

        if (!styleElem) {
          return;
        }

        // If we have a ShadowRoot, than the current rule is targeting a ShadowDOM.
        /*
                    Note
                    When we'll provide support for AdoptedStyleSheet in the Document
                    we need an extra variable to differentiate it with a Shadow DOM
                */
        if (styleElem.shadowRoot) {
          styleElem = styleElem.shadowRoot.querySelector('style');
        }

        styleSheet = styleElem.sheet;

        if (ruleSet.index >= styleSheet.cssRules.length) {
          return;
        }

        styleSheet.deleteRule(ruleSet.index);
      } catch (e) {
        /* eslint-disable no-console */
        // The following line is required for debugging purposes.
        // nosemgrep
        console.error(e, ruleSet);
        /* eslint-enable no-console */
      }
    });

    if (isScreenshotMode()) {
      hydrateStyleTag(event);
    }
  }

  function handleAdoptedStyleSheets(event, startPageContentIdentifier, recordingId) {
    const node = window.iframeController
      .getTreeMirror(recordingId, startPageContentIdentifier)
      .getNodeById(event.nodeId);

    const parent = node ?? getElementByCustomSelector(event.parentSelector);
    const shadowRoot = parent.shadowRoot;

    // An element could adopt an hybrid approach to CSS e.g. style tag + adopted style stylesheets
    // For this reason, we select only the style element(s) we tag as constructed
    const styles = shadowRoot.querySelectorAll(`style[id^="${CONSTRUCTED_CSS_ID_PREFIX}"`);
    if (styles.length) {
      styles.forEach((s) => s.remove());
    }

    const styleTags = getStyleElementsBy(event.sheets);

    shadowRoot.appendChild(styleTags);
  }

  function handleMutationEvent(event, startPageContentIdentifier, recordingId) {
    let mutationParentNodeID;

    try {
      // Used for older recordings with full variable names.
      if (typeof event.removed !== 'undefined') {
        event.a = event.removed;
      }
      if (typeof event.addedOrMoved !== 'undefined') {
        event.b = event.addedOrMoved;
      }
      if (typeof event.attributes !== 'undefined') {
        event.c = event.attributes;
      }
      if (typeof event.text !== 'undefined') {
        event.d = event.text;
      }
      // If an array is not set, set it manually.
      if (typeof event.a === 'undefined') {
        event.a = []; // removed
      }
      if (typeof event.b === 'undefined') {
        event.b = []; // addedOrMoved
      }
      if (typeof event.c === 'undefined') {
        event.c = []; // attributes
      }
      if (typeof event.d === 'undefined') {
        event.d = []; // text
      }

      // :hover psuedo class replacement when injecting style elements to the DOM.
      event.b.forEach(function (mutation) {
        mutationParentNodeID = mutation.parentNode && mutation.parentNode.id;

        // Store all style tags within an array.
        if (mutation.tagName === 'STYLE') {
          mutationStyleTagIDs.push(mutation.id);
        }

        // If we are appending the textContent to one of the style tags we stored earlier,
        // update all :hover references to use a attribute instead.
        // We use attribute instead of a class name to prevent unnecessary repaints.
        if (mutation.textContent && mutationStyleTagIDs.indexOf(mutationParentNodeID) > -1) {
          mutation.textContent = mutation.textContent.replace(
            /:hover/g,
            '[' + cssHoverAttribute + ']',
          );
        }
      });

      window.iframeController.applyChanged(
        recordingId,
        startPageContentIdentifier,
        event.a,
        event.b,
        event.c,
        event.d,
        event.timestamp,
      );
    } catch (e) {
      /* eslint-disable no-console */
      // The following line is required for debugging purposes.
      // nosemgrep
      console.error(e, event);
      /* eslint-enable no-console */
    }
  }

  /**
   * Expand a select element programmatically.
   *
   * Original source from https://code.google.com/p/expandselect/
   */
  function expandSelect(selectElement) {
    const maxOptionsVisible = 20;
    const offSet = h.getOffset(selectElement);

    let newSelectElement = selectElement.cloneNode(true);

    newSelectElement.size = Math.min(maxOptionsVisible, selectElement.options.length);

    newSelectElement.removeAttribute('id');

    newSelectElement.style.position = 'absolute';
    newSelectElement.style.left = offSet.left + 'px';
    newSelectElement.style.top = offSet.top + 'px';
    newSelectElement.style.width = selectElement.offsetWidth;
    newSelectElement.style.height = 'auto';
    newSelectElement.style.zIndex = '1000000';

    newSelectElement = document.querySelector(BASE_EL_ID).appendChild(newSelectElement);

    selectElement.doBlur = function () {
      newSelectElement.parentNode.removeChild(newSelectElement);
      selectElement.blur();
    };
    selectElement.focus();
  }

  /**
   * Close a select element.
   */
  function closeSelect() {
    expandedSelect.doBlur();
    expandedSelect = null;
  }

  /**
   * Patches recordings created before (2021-10-13) with broken selectors
   * TODO: safe to remove from 2022-10-13
   */
  function patchOldSelector(elementsSelector) {
    // Replace spaces in if with `\\\s`
    if (elementsSelector.startsWith('#')) {
      elementsSelector = elementsSelector.replaceAll(/\s/g, (match) => `\\${match[0]}`);
    }

    // Escape '<'
    var illegalCssCharsNew = /(<)/g;
    elementsSelector = elementsSelector.replaceAll(illegalCssCharsNew, (match) => `\\${match[0]}`);

    // Prefix number selectors that start with number
    // f.e. `#2id` => `#\\32 id`
    // f.e. `.2class` => `.\\32 class`
    elementsSelector = elementsSelector.replaceAll(
      /(#|\.)(\d)/g,
      (match) => `${match[0]}\\3${match[1]} `,
    );
    return elementsSelector;
  }

  /**
   *  Get a shadow dom element
   *  In case the selector contains the `hj-shadow:` prefix it means
   *  that we are trying to target an element living inside a shadow dom
   */
  function getShadowDomElement(selector, index) {
    const capture = new RegExp(`^(.*?${HJ_SHADOW_PREFIX}.*?)>(.*)$`, 'i');

    const processNestedSelector = (root, currentSelector) => {
      let foundElements = [];
      const [, parentDomSelector, childDomSelector] = currentSelector.match(capture);

      root.querySelectorAll(parentDomSelector.replace(HJ_SHADOW_PREFIX, '')).forEach((element) => {
        if (element.shadowRoot) {
          if (childDomSelector && childDomSelector.includes(HJ_SHADOW_PREFIX)) {
            foundElements.push(...processNestedSelector(element.shadowRoot, childDomSelector));
          } else {
            foundElements.push(...element.shadowRoot.querySelectorAll(childDomSelector));
          }
        }
      });

      return foundElements;
    };

    const matchedElements = processNestedSelector(sceneDocument, selector);

    return matchedElements[index];
  }

  /**
   * Get an element using one of our custom selectors.
   */
  function getElementByCustomSelector(selector) {
    const elementIndex = parseInt(selector.split(':')[0]);
    const elementsSelector = selector.substring(selector.indexOf(':') + 1);

    try {
      // if this selector targets a shadow dom element
      if (elementsSelector.includes(HJ_SHADOW_PREFIX)) {
        return getShadowDomElement(elementsSelector, elementIndex);
      }

      return sceneDocument.querySelectorAll(elementsSelector)[elementIndex];
    } catch (error) {
      try {
        const patchedSelector = patchOldSelector(elementsSelector);
        return sceneDocument.querySelectorAll(patchedSelector)[elementIndex];
      } catch (error) {
        window.Sentry.captureMessage(`Player invalid querySelectorAll: ${elementsSelector}`);
      }
    }
  }

  /**
   * Update the coordinates of the cursor and then draw it on canvas.
   *  Coords should be relative to the viewport.
   */
  function updateCursorCoordinates(coords, currentTime) {
    const cursor = window.document.getElementById('preview-cursor');
    const scrollLeft = sceneDocument.body.scrollLeft || sceneWindow.pageXOffset;
    const scrollTop = sceneDocument.body.scrollTop || sceneWindow.pageYOffset;

    // Adjusted coords = coords relative to the viewport + scroll height + any adjustments needed
    const newCoords = {
      x: coords.x + scrollLeft + mouseMoveCorrectionOffsets.x,
      y: coords.y + scrollTop + mouseMoveCorrectionOffsets.y,
    };

    if (lastMouseMoveCoords !== null) {
      // If a scroll has happened since we last updated the coordinates set the old coordinates to
      // be the new coordinates so we get a new trace line only showing mouse moves.
      if (hasScrolled) {
        lastMouseMoveCoords = newCoords;
        hasScrolled = false;
      }

      // Place the new line if it shouldn't have been hidden.
      if (shouldTrailBeUpdated(currentTime)) {
        const cursorHeight = parseInt(cursor.clientHeight, 10);
        const cursorWidth = parseInt(cursor.clientWidth, 10);
        // Update the cursor accordingly. Use the coordinates that are relative to the viewport
        // and correct them if needed.
        // Slightly readjust the position of the marker so that it's centered on the click.
        const finalCoords = {
          x: coords.x + mouseMoveCorrectionOffsets.x - cursorWidth / 2,
          y: coords.y + mouseMoveCorrectionOffsets.y - cursorHeight / 2,
        };
        cursor.style.transform = `translate(${finalCoords.x}px, ${finalCoords.y}px)`;

        createLine(lastMouseMoveCoords.x, lastMouseMoveCoords.y, newCoords.x, newCoords.y);
      }
    }

    lastMouseMoveCoords = newCoords;
  }

  function createLine(startX, startY, endX, endY) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');

    line.setAttribute('x1', startX);
    line.setAttribute('y1', startY);
    line.setAttribute('x2', endX);
    line.setAttribute('y2', endY);
    line.setAttribute('stroke', MOUSE_TRAIL_COLOR);
    line.setAttribute('stroke-width', 4);
    line.setAttribute('class', '_hjRecordingTrail ' + MOUSE_TRAIL_ANIMATION.PLAY_CLASS);
    line.style.animation = `fadeOut ${MOUSE_TRAIL_ANIMATION.DURATION / playbackSpeed}s ${
      MOUSE_TRAIL_ANIMATION.DELAY / playbackSpeed
    }s 1`;

    document.querySelector('#svg-actions').appendChild(line);
  }

  function pauseAnimationIfUserInactive(currentTime) {
    const isUserInactive =
      currentTime - lastMouseMoveTime >= MOUSE_TRAIL_ANIMATION.PAUSE_DUE_TO_INACTIVITY_TIME * 1000;
    if (!animationPaused && isUserInactive) {
      setTrailAnimationPaused(true);
    } else if (animationPaused && !isUserInactive) {
      setTrailAnimationPaused(false);
    }
  }

  function setTrailAnimationPaused(pause) {
    const action = pause ? 'add' : 'remove';

    animationPaused = !!pause;
    document.body.classList[action](MOUSE_TRAIL_ANIMATION.PAUSE_CLASS);
  }

  /**
   *  Fetches scene document's dimensions and adjusts the svg's size if needed.
   */
  function adjustSVGSize() {
    const newSVGSize = getSceneDocumentDimensions();
    const svgActions = document.querySelector('#svg-actions');

    for (var dimension in newSVGSize) {
      if (svgActions.getAttribute(dimension) !== newSVGSize[dimension]) {
        svgActions.setAttribute(dimension, newSVGSize[dimension]);
      }
    }
  }

  /**
   * After `MOUSE_TRAIL_ANIMATION.DELAY` seconds we hide existing lines and
   * markers, so at any given time we want to see that part of the trail formed
   * by events that have occurred up to `animationDelayInMs` ago
   */
  function shouldTrailBeUpdated(currentTime) {
    const animationDelayInMs =
      (MOUSE_TRAIL_ANIMATION.DELAY + MOUSE_TRAIL_ANIMATION.DURATION) * 1000;

    return currentTime >= playbackStartTime - animationDelayInMs;
  }

  /**
   * Click 'blobs' on playback click events.
   */
  function createClickMarker(coords) {
    // -15 (half the marker size) to center marker.
    const left = Math.round(coords.x - 15) + 'px';
    const top = Math.round(coords.y - 15) + 'px';

    let marker = document.createElement('div');

    marker.style.pointerEvents = 'none';
    marker.style.border = '0px solid transparent';
    marker.style.boxSizing = 'border-box';
    marker.style.backgroundColor = '#DF4C2B';
    marker.style.height = '30px';
    marker.style.width = '30px';
    marker.style.position = 'absolute';
    marker.style.zIndex = 2147483647;
    marker.style.opacity = 0.4;
    marker.style.borderRadius = '50%';
    marker.style.backgroundClip = 'padding-box';
    marker.style.left = left;
    marker.style.top = top;
    marker.style.transitionDuration = '.5s';
    marker.className = '_hjRecordingTrail ' + MOUSE_TRAIL_ANIMATION.PLAY_CLASS;
    marker.style.animation = `fadeOut ${MOUSE_TRAIL_ANIMATION.DURATION / playbackSpeed}s ${
      MOUSE_TRAIL_ANIMATION.DELAY / playbackSpeed
    }s 1`;

    // Add a click marker when there is a click.
    marker = document.querySelector(BASE_EL_ID).appendChild(marker);

    // Delays style change to allow for dom update.
    setTimeout(function () {
      marker.style.border = '9px solid transparent';
      marker.style.opacity = 0.8;
    }, 10);
  }

  /**
   * Clears all click markers and lines from the document.
   */
  function clearTrail() {
    const markers = document.getElementsByClassName('_hjRecordingTrail');

    for (let i = markers.length - 1; i >= 0; i--) {
      const marker = markers[i];
      marker.parentNode.removeChild(marker);
    }
  }

  function getSceneDocumentDimensions() {
    if (!sceneDocument || !sceneDocument.body) {
      return;
    }

    const body = sceneDocument.body;
    const html = sceneDocument.documentElement;
    const width = Math.max(
      body.scrollWidth,
      body.offsetWidth,
      html.clientWidth,
      html.scrollWidth,
      html.offsetWidth,
    );
    const height = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.clientHeight,
      html.scrollHeight,
      html.offsetHeight,
    );

    return {
      width,
      height,
    };
  }

  function onSVGAnimationEnd(event) {
    event?.target?.parentNode?.removeChild(event.target);
  }

  function initializePlayback(recordingId, startPageContentIdentifier, iframe) {
    document.querySelector('#svg-actions').addEventListener('animationend', onSVGAnimationEnd);

    const iframeController = window.iframeController;
    iframeController.showIframe(recordingId, startPageContentIdentifier).then(() => {
      sceneDocument = iframe.contentWindow.document;
      sceneWindow = iframe.contentWindow;
      adjustSVGSize();

      window.playbackModuleHasLoadedThePage = true;
    });
  }

  let isForced = false;

  function initializeWhenIframeReady(recordingId, startPageContentIdentifier) {
    isForced = false;
    const iframeController = window.iframeController;
    iframeController.getIframe(recordingId, startPageContentIdentifier).then((iframe) => {
      // If iframe already ready initialize immediately.
      // Note, we are not relying on readyState because it works diferrently across browsers.
      if (iframe.getAttribute('data-loaded') === 'true') {
        if (!isForced) {
          // If iframe was forced we don't want to initialize again.
          initializePlayback(recordingId, startPageContentIdentifier, iframe);
        }
      } else {
        // Wait until iframe is ready.
        iframe.addEventListener('load', () => {
          // If iframe was forced we don't want to initialize again.
          if (!isForced) {
            initializePlayback(recordingId, startPageContentIdentifier, iframe);
          }
        });
      }
    });
    cursor = window.document.getElementById('preview-cursor');
  }

  /**
   * PUBLIC FUNCTIONS
   *
   *  */

  function pause() {
    // User has paused recording, we have to pause trail animations.
    setTrailAnimationPaused(true);
  }

  function resume() {
    // User has resumed recording, we have to resume trail animations.
    setTrailAnimationPaused(false);
  }

  function adjustSpeed(currentSpeed) {
    // Recording speed adjusted, save new speed as it affects trail
    // animation delay.
    playbackSpeed = currentSpeed;
  }

  function inactiveTick(currentTime) {
    // There were no events to be processed during this tick
    // check if we need to pause trail animation due to inactivity.
    pauseAnimationIfUserInactive(currentTime);
    onInactiveTickDoneCb();
  }

  function adjustRecordingWidth(width) {
    currentRecordingWidth = width;
  }

  function retrieveHtml() {
    // Retrieve the current doctype + HTML.
    const serializer = new XMLSerializer();
    const doctype = serializer.serializeToString(sceneDocument.doctype);
    const html = serializer.serializeToString(sceneDocument.getElementsByTagName('html')[0]);
    const data = doctype + html;

    onRetrieveHtmlResponseCb(data);
  }

  function handleEvent(playbackEvent, startPageContentIdentifier, recordingId) {
    switch (playbackEvent.type) {
      case 'key_press':
        handleKeyPressEvent(playbackEvent);
        break;
      case 'select_change':
        handleSelectChangeEvent(playbackEvent);
        break;
      case 'input_choice_change':
        handleInputChoiceChangeEvent(playbackEvent);
        break;
      case 'mouse_move':
        lastMouseMoveTime = playbackEvent.time_in_timeline;
        handleMouseMoveEvent(playbackEvent);
        break;
      case 'mouse_click':
        handleMouseClickEvent(playbackEvent);
        break;
      case 'mutation':
        handleMutationEvent(playbackEvent, startPageContentIdentifier, recordingId);
        // Mutations can affect scene document's size, and
        // in case they do, we have to adjust the size of the svg
        // that holds the mouse trail.
        adjustSVGSize();
        break;
      case 'scroll':
        handleScrollEvent(playbackEvent);
        break;
      case 'inserted_rule':
        handleInsertedRule(playbackEvent, startPageContentIdentifier, recordingId);
        break;
      case 'deleted_rule':
        handleDeletedRule(playbackEvent, startPageContentIdentifier, recordingId);
        break;
      case 'adopted_style_sheets':
        handleAdoptedStyleSheets(playbackEvent, startPageContentIdentifier, recordingId);
        break;
    }
  }

  function sendEventsData(
    playbackEventsForTick,
    windowWidth,
    { fromPosition, toPosition, ...rest },
  ) {
    if (typeof fromPosition !== 'undefined' && typeof toPosition !== 'undefined') {
      const page = rest.page;
      const recordingId = rest.recordingId;
      const startPageContentIdentifier = rest.startPageContentIdentifier;
      let filteredEvents = [];

      if (!currentRecordingWidth && windowWidth) {
        adjustRecordingWidth(windowWidth);
      }

      if (rest.skipNonMutationEvents) {
        filteredEvents = skipEvents(playbackEventsForTick, fromPosition, toPosition);
      }

      if (filteredEvents.length > 0) {
        processEvents(
          page,
          recordingId,
          startPageContentIdentifier,
          filteredEvents,
          fromPosition,
          filteredEvents.length - 1,
        );
      } else {
        processEvents(
          page,
          recordingId,
          startPageContentIdentifier,
          playbackEventsForTick,
          fromPosition,
          toPosition,
        );
      }
    }
  }

  function skipEvents(playbackEventsForTick, fromPosition, toPosition) {
    const playbackEventsFromToPosition = playbackEventsForTick.slice(fromPosition, toPosition + 1);
    const mutationTypesToInclude = [
      'mutation',
      'inserted_rule',
      'deleted_rule',
      'adopted_style_sheets',
    ];
    let eventTypes = [];
    let filteredEvents = [];

    playbackEventsFromToPosition.forEach((event) => {
      eventTypes.push(event.type);
      // Apart from the DOM mutation event itself there are other
      // types of mutations that  we need to preserve for playback accuracy
      if (mutationTypesToInclude.includes(event.type)) {
        filteredEvents.push(event);
      }
    });

    // we need to store the last scroll and
    // mouse_move events for playback accuracy
    const lastMouseMoveIndex = eventTypes.lastIndexOf('mouse_move');
    const lastScrollIndex = eventTypes.lastIndexOf('scroll');

    if (lastMouseMoveIndex >= 0) {
      const lastMouseMove = playbackEventsFromToPosition[lastMouseMoveIndex];
      filteredEvents.push(lastMouseMove);
    }

    if (lastScrollIndex >= 0) {
      const lastScroll = playbackEventsFromToPosition[lastScrollIndex];
      filteredEvents.push(lastScroll);
    }

    return filteredEvents;
  }

  function processEvents(
    page,
    recordingId,
    startPageContentIdentifier,
    playbackEventsForTick,
    fromPosition,
    toPosition,
  ) {
    if (typeof playbackEventsForTick !== 'string') {
      for (let i = fromPosition; i <= toPosition; i++) {
        const playbackEvent = playbackEventsForTick[i];
        // Check when user lastly moved his mouse,
        // if user has been inactive we have to pause trail animations
        pauseAnimationIfUserInactive(playbackEvent.time_in_timeline);

        try {
          handleEvent(playbackEvent, startPageContentIdentifier, recordingId);
        } catch (error) {
          window.Sentry.captureException(error);
        }

        // Inform the recording controller that we are done processing a set of events.
        onEventsProcessedCb({ page, toPosition });
      }
    }
  }

  function forcePlayback(recordingId, startPageContentIdentifier) {
    isForced = true;
    const iframeController = window.iframeController;
    iframeController.getIframe(recordingId, startPageContentIdentifier).then((iframe) => {
      initializePlayback(recordingId, startPageContentIdentifier, iframe);
    });
  }

  function run(
    page,
    startPageContentIdentifier,
    previousStartPageContentIdentifier,
    recordingId,
    device,
    startTime,
  ) {
    currentDevice = device;
    window.playbackModuleHasLoadedThePage = false;
    playbackStartTime = startTime;
    const iframeController = window.iframeController;
    if (previousStartPageContentIdentifier) {
      iframeController.removeIframe(recordingId, previousStartPageContentIdentifier);
    }
    iframeController.maybePrerenderIframes(recordingId, startPageContentIdentifier).then(() => {
      initializeWhenIframeReady(recordingId, startPageContentIdentifier);
    });
  }

  function onInactiveTickDone(cb) {
    onInactiveTickDoneCb = cb;
  }

  function onRetrieveHtmlResponse(cb) {
    onRetrieveHtmlResponseCb = cb;
  }

  function onEventsProcessed(cb) {
    onEventsProcessedCb = cb;
  }

  return {
    adjustSVGSize,
    run,
    skipEvents,
    forcePlayback,
    sendEventsData,
    onInactiveTickDone,
    onRetrieveHtmlResponse,
    onEventsProcessed,
    pause,
    clearTrail,
    adjustSpeed,
    inactiveTick,
    adjustRecordingWidth,
    retrieveHtml,
    resume,
    getElementByCustomSelector, // we are exporting this for testing purposes
  };
};
